import React, { type ReactNode, useMemo } from 'react';

import { useConfig } from '../../contexts/config-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { formatCurrency } from '../../utils/format-currency';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type FixedPaymentFieldProps } from './fixed-payment-field-props';

export type FixedPaymentProps = {
  children: ReactNode;
};

export const FixedPayment = ({ children }: FixedPaymentProps) => {
  const { id, validation, FieldLayout } =
    useFieldPropsV2<FixedPaymentFieldProps>();

  const { currency, regionalFormat } = useConfig();

  const paymentValidation = useMemo(
    () => validation?.predefined?.paymentOptions?.products?.[0],
    [validation],
  );

  const fixedPaymentValue = paymentValidation?.fixedPriceOptions?.price;

  const labelElement = findChildOfType(children, FixedPayment.Label);
  const amountElement = findChildOfType(children, FixedPayment.Amount);
  const descriptionElement = findChildOfType(
    children,
    FixedPayment.Description,
  );
  const errorElement = findChildOfType(children, FixedPayment.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() =>
        applyInternalProps<AmountInternalProps>(amountElement, {
          price: fixedPaymentValue,
          currencyCode: currency?.code,
          locale: regionalFormat,
        })
      }
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type AmountProps = {
  className?: string;
};

type AmountInternalProps = {
  price?: string;
  currencyCode?: string;
  locale?: string;
};

const Amount = ({ className, ...internal }: AmountProps) => {
  const { price, currencyCode, locale } = internal as AmountInternalProps;
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const formattedValue = formatCurrency({
    amount: price,
    currency: currencyCode,
    locale,
  });

  return (
    <span
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      data-hook="fixed-payment-value"
    >
      {formattedValue}
    </span>
  );
};

type FixedPaymentLabelProps = {
  className?: string;
};

const FixedPaymentLabel = ({ className }: FixedPaymentLabelProps) => {
  return <Label className={className}>{null}</Label>;
};

FixedPayment.Label = FixedPaymentLabel;
FixedPayment.Amount = Amount;
FixedPayment.Description = Description;
FixedPayment.Error = Error;
